import { useRef, useEffect, useState, useCallback } from "react";
import { ProductDetail } from "@/mocks/productDetails";
import SearchSuggestions from "./SearchSuggestions";

interface SearchBarProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  products?: ProductDetail[];
}

export default function SearchBar({
  value,
  onChange,
  placeholder = "ابحث عن منتج...",
  products = [],
}: SearchBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  // Sync external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Ctrl+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setShowSuggestions(true);
      }
      if (e.key === "Escape") {
        setShowSuggestions(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setInputValue(v);
    onChange(v);
    setShowSuggestions(v.trim().length > 0);
  }, [onChange]);

  const handleFocus = useCallback(() => {
    if (inputValue.trim().length > 0) {
      setShowSuggestions(true);
    }
  }, [inputValue]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setShowSuggestions(false);
    }
  }, []);

  const handleSelect = useCallback((val: string) => {
    setInputValue(val);
    onChange(val);
    setShowSuggestions(false);
  }, [onChange]);

  const handleClear = useCallback(() => {
    setInputValue("");
    onChange("");
    setShowSuggestions(false);
    inputRef.current?.focus();
  }, [onChange]);

  return (
    <div className="relative w-full">
      {/* Input */}
      <div className="relative">
        <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none z-10">
          <i className="ri-search-line text-gray-500 text-lg"></i>
        </div>

        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full bg-[#141414] border border-white/10 text-white placeholder-gray-600 rounded-xl pr-12 pl-24 py-3.5 text-sm focus:outline-none focus:border-amber-400/50 transition-colors"
          style={{ fontFamily: "'Cairo', sans-serif" }}
          dir="rtl"
          autoComplete="off"
        />

        {/* Right side: clear + shortcut hint */}
        <div className="absolute inset-y-0 left-3 flex items-center gap-2">
          {inputValue && (
            <button
              onClick={handleClear}
              className="flex items-center justify-center w-6 h-6 text-gray-500 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          )}
          {!inputValue && (
            <kbd className="hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 bg-white/5 border border-white/10 rounded text-gray-600 text-xs whitespace-nowrap">
              <span>⌘</span><span>K</span>
            </kbd>
          )}
        </div>
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && products.length > 0 && (
        <SearchSuggestions
          query={inputValue}
          products={products}
          onSelect={handleSelect}
          onClose={() => setShowSuggestions(false)}
        />
      )}
    </div>
  );
}
